import { PageHeader } from "@/components/layout/header-provider";
import { QuickSettingsDialog } from "@/components/organization/quick-settings-dialog";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { api as trpc } from "@/lib/trpc";
import { Card } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import AccountDatatable, {
  type AccountItem,
  ManualFilter,
} from "@/components/shadcn-studio/blocks/datatable-account";
import type {
  ColumnFiltersState,
  PaginationState,
} from "@tanstack/react-table";

export const Route = createFileRoute("/admin/accounts")({
  component: AccountsPage,
});

function AccountsPage() {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [search, setSearch] = useState("");
  // We'll manage filters via table state but sync with our query params
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [editingOrg, setEditingOrg] = useState<AccountItem | null>(null);

  const debouncedSearch = useDebounce(search, 500);

  // Extract type filter from columnFilters
  const typeFilter = columnFilters.find((f) => f.id === "type")
    ?.value as string;

  const { data, isLoading } = useQuery(
    trpc.admin.getAccounts.queryOptions(
      {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch || undefined,
        type: typeFilter === "all" ? undefined : typeFilter,
      },
      {
        placeholderData: (prev) => prev,
      },
    ),
  );

  // Sync search state if needed (AccountDatatable doesn't have built-in search input yet,
  // we can keep the one in the page or move it to the datatable.
  // shadcn datatable usually has search inside.
  // For now let's wrap the datatable and pass everything.
  // Actually, AccountDatatable component I created DOES NOT have a search input.
  // I should probably add one or keep it external.
  // The design in datatable-user.tsx HAD filters but not search.
  // Wait, datatable-user.tsx HAD search input?
  // Checking datatable-user.tsx (Step 1619) -> it confirms NO search input in the component itself.
  // The search input was in UsersPage (Step 1600).
  // So I should keep the search input here or move it into AccountDatatable.
  // The user asked for "same treatment".
  // `datatable-user` example had filters in a separate div above table.
  // I'll stick to passing props.

  return (
    <div className="flex flex-col gap-6 p-6 max-md:p-0 h-full">
      <PageHeader>
        <span className="font-semibold">Accounts</span>
      </PageHeader>

      <Card className="flex-1 flex flex-col overflow-hidden max-md:border-0 max-md:shadow-none max-md:bg-transparent max-md:rounded-none">
        {/* Search Input - keeping it external like in UsersPage initially or move to Datatable?
            My `AccountDatatable` has a "Filter" header inside.
            Let's see: `AccountDatatable` renders a filter section (lines 280+).
            It does NOT render a search bar.
            So I should render the search bar HERE, or add it to `AccountDatatable`.
            Given `AccountDatatable` is a "block", maybe it should handle it?
            But `UserDatatable` didn't have it.
            I will render it here above the table, similar to how `UsersPage` did it in step 1600.
        */}
        <div className="p-4 border-b flex items-center gap-4 justify-between bg-card text-card-foreground">
          {/* Re-using the search input from previous implementation */}
          <div className="flex items-center gap-2 w-full md:max-w-sm relative">
            <input
              placeholder="Search accounts..."
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 pl-9"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </div>

          <div className="w-full md:w-50">
            <ManualFilter
              label="Type"
              value={
                (columnFilters.find((f) => f.id === "type")?.value as string) ||
                "all"
              }
              options={[
                { value: "agency", label: "Agency" },
                { value: "creator", label: "Creator" },
              ]}
              onValueChange={(val) => {
                const newFilters =
                  columnFilters.filter((f) => f.id !== "type") || [];
                if (val && val !== "all") {
                  newFilters.push({ id: "type", value: val });
                }
                setColumnFilters(newFilters);
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <AccountDatatable
            data={
              (data?.data || []).map((item) => ({
                ...item,
                createdAt: new Date(item.createdAt).toISOString(),
                updatedAt: new Date(item.updatedAt).toISOString(),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                metadata: (item.metadata || {}) as any,
              })) as AccountItem[]
            }
            pageCount={data?.meta.totalPages || 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            columnFilters={columnFilters}
            onColumnFiltersChange={(filters) => {
              setColumnFilters(filters);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            isLoading={isLoading}
            onEdit={(org) => setEditingOrg(org)}
          />
        </div>
      </Card>
      {/* Map AccountItem to the shape expected by QuickSettingsDialog */}
      {editingOrg && (
        <QuickSettingsDialog
          open={!!editingOrg}
          onOpenChange={(open) => !open && setEditingOrg(null)}
          organization={{
            id: editingOrg.id,
            slug: editingOrg.slug,
            name: editingOrg.name,
            metadata: editingOrg.metadata,
          }}
        />
      )}
    </div>
  );
}
